import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { postPartyMovement, syncClientOpeningBalance } from "@/lib/party-ledger";
import type {
  AddressFormValues,
  ClientFormValues,
  ContactFormValues,
  NoteFormValues,
  ReminderFormValues,
} from "@/features/clients/schema";
import type {
  Client,
  ClientAddress,
  ClientArMovement,
  ClientContact,
  ClientFile,
  ClientListRow,
  ClientNote,
  ClientReminder,
} from "@/features/clients/types";

function parseGroups(raw: string | undefined) {
  if (!raw?.trim()) return [] as string[];
  return raw
    .split(",")
    .map((g) => g.trim())
    .filter(Boolean);
}

const text = (value: string | undefined) => value?.trim() || null;

function normalizeClient(values: ClientFormValues) {
  return {
    name: values.name.trim(),
    code: values.code.trim().toUpperCase(),
    client_type: values.client_type,
    industry_segment: text(values.industry_segment),
    lead_source: text(values.lead_source),
    onboarded_on: text(values.onboarded_on),
    is_active: values.is_active,

    work_phone: text(values.work_phone),
    email: text(values.email),
    website: text(values.website),
    groups: parseGroups(values.groups),
    show_primary_contact_on_docs: values.show_primary_contact_on_docs,

    addr1: values.addr1.trim(),
    addr2: text(values.addr2),
    state: values.state.trim(),
    city: values.city.trim(),
    pincode: values.pincode.trim(),

    gst_treatment: values.gst_treatment,
    gst_no: values.gst_no?.trim().toUpperCase() || null,
    pan_no: values.pan_no?.trim().toUpperCase() || null,
    cin_no: values.cin_no?.trim().toUpperCase() || null,
    msme_udyam_no: text(values.msme_udyam_no),
    place_of_supply: text(values.place_of_supply) ?? values.state.trim(),
    tds_applicable: values.tds_applicable,
    tcs_applicable: values.tcs_applicable,

    credit_limit: values.credit_limit,
    credit_days: values.credit_days,
    payment_terms: values.payment_terms,
    price_list: values.price_list,
    rate_basis: values.rate_basis,
    currency: values.currency.trim().toUpperCase(),
    opening_balance: values.opening_balance,
    opening_balance_as_of: text(values.opening_balance_as_of),

    delivery_terms: values.delivery_terms,
    freight_terms: values.freight_terms,
    preferred_transporter: text(values.preferred_transporter),
    packing_type: values.packing_type,
    dispatch_instructions: text(values.dispatch_instructions),

    requires_test_certificate: values.requires_test_certificate,
    requires_third_party_inspection: values.requires_third_party_inspection,
    quality_standard: text(values.quality_standard),
    tolerance_notes: text(values.tolerance_notes),

    notes: text(values.notes),
  };
}

/** Maps a persisted client back onto form defaults so edit screens hydrate cleanly. */
export function clientToForm(client: Client): ClientFormValues {
  return {
    name: client.name,
    code: client.code,
    client_type: client.client_type,
    industry_segment: client.industry_segment ?? "",
    lead_source: client.lead_source ?? "",
    onboarded_on: client.onboarded_on ?? "",
    is_active: client.is_active,

    work_phone: client.work_phone ?? "",
    email: client.email ?? "",
    website: client.website ?? "",
    groups: (client.groups ?? []).join(", "),
    show_primary_contact_on_docs: client.show_primary_contact_on_docs,

    addr1: client.addr1,
    addr2: client.addr2 ?? "",
    city: client.city,
    state: client.state,
    pincode: client.pincode,

    gst_treatment: client.gst_treatment,
    gst_no: client.gst_no ?? "",
    pan_no: client.pan_no ?? "",
    cin_no: client.cin_no ?? "",
    msme_udyam_no: client.msme_udyam_no ?? "",
    place_of_supply: client.place_of_supply ?? "",
    tds_applicable: client.tds_applicable,
    tcs_applicable: client.tcs_applicable,

    credit_limit: Number(client.credit_limit ?? 0),
    credit_days: client.credit_days ?? 0,
    payment_terms: client.payment_terms,
    price_list: client.price_list,
    rate_basis: client.rate_basis,
    currency: client.currency ?? "INR",
    opening_balance: Number(client.opening_balance ?? 0),
    opening_balance_as_of: client.opening_balance_as_of ?? "",

    delivery_terms: client.delivery_terms,
    freight_terms: client.freight_terms,
    preferred_transporter: client.preferred_transporter ?? "",
    packing_type: client.packing_type,
    dispatch_instructions: client.dispatch_instructions ?? "",

    requires_test_certificate: client.requires_test_certificate,
    requires_third_party_inspection: client.requires_third_party_inspection,
    quality_standard: client.quality_standard ?? "",
    tolerance_notes: client.tolerance_notes ?? "",

    notes: client.notes ?? "",
  };
}

function money(value: unknown) {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isNaN(n) ? 0 : n;
}

function hydrateClient(raw: Client): Client {
  return {
    ...raw,
    groups: raw.groups ?? [],
    credit_limit: money(raw.credit_limit),
    credit_days: money(raw.credit_days),
    outstanding_balance: money(raw.outstanding_balance),
    overdue_balance: money(raw.overdue_balance),
    opening_balance: money(raw.opening_balance),
  };
}

export function useClients() {
  return useQuery({
    queryKey: ["clients"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<ClientListRow[]> => {
      const { data: clients, error } = await supabase
        .from("clients")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      const list = (clients ?? []) as Client[];
      if (!list.length) return [];

      const ids = list.map((c) => c.id);
      const { data: contacts, error: cErr } = await supabase
        .from("client_contacts")
        .select("client_id, name, email, mobile, is_primary")
        .in("client_id", ids)
        .eq("is_primary", true);
      if (cErr) throw cErr;

      const byClient = new Map(
        (contacts ?? []).map((c) => [
          c.client_id as string,
          { name: c.name as string, email: c.email as string | null, mobile: c.mobile as string | null },
        ]),
      );

      return list.map((client) => ({
        ...hydrateClient(client),
        primary_contact: byClient.get(client.id) ?? null,
      }));
    },
  });
}

export function useClient(clientId: string | undefined) {
  return useQuery({
    queryKey: ["clients", clientId],
    enabled: isSupabaseConfigured && Boolean(clientId),
    queryFn: async (): Promise<Client> => {
      const { data, error } = await supabase.from("clients").select("*").eq("id", clientId!).single();
      if (error) throw error;
      return hydrateClient(data as Client);
    },
  });
}

export function useClientCounts(clientId: string | undefined) {
  return useQuery({
    queryKey: ["clients", clientId, "counts"],
    enabled: isSupabaseConfigured && Boolean(clientId),
    queryFn: async () => {
      const id = clientId!;
      const [contacts, addresses, notes, reminders, files] = await Promise.all([
        supabase.from("client_contacts").select("id", { count: "exact", head: true }).eq("client_id", id),
        supabase.from("client_addresses").select("id", { count: "exact", head: true }).eq("client_id", id),
        supabase.from("client_notes").select("id", { count: "exact", head: true }).eq("client_id", id),
        supabase.from("client_reminders").select("id", { count: "exact", head: true }).eq("client_id", id),
        supabase.from("client_files").select("id", { count: "exact", head: true }).eq("client_id", id),
      ]);
      return {
        contacts: contacts.count ?? 0,
        addresses: addresses.count ?? 0,
        notes: notes.count ?? 0,
        reminders: reminders.count ?? 0,
        files: files.count ?? 0,
      };
    },
  });
}

export function useCreateClient() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (values: ClientFormValues) => {
      const payload = {
        ...normalizeClient(values),
        created_by: user?.id ?? null,
        updated_by: user?.id ?? null,
      };
      const { data, error } = await supabase.from("clients").insert(payload).select("*").single();
      if (error) throw error;
      const client = data as Client;
      await syncOpeningBalanceMovement(client.id, values.opening_balance, text(values.opening_balance_as_of));
      return client;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      await queryClient.invalidateQueries({ queryKey: ["client_ar_movements"] });
      toast.success("Client created");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to create client"),
  });
}

export function useUpdateClient() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, values }: { id: string; values: ClientFormValues }) => {
      const payload = {
        ...normalizeClient(values),
        updated_by: user?.id ?? null,
      };
      const { data, error } = await supabase.from("clients").update(payload).eq("id", id).select("*").single();
      if (error) throw error;
      await syncOpeningBalanceMovement(id, values.opening_balance, text(values.opening_balance_as_of));
      return data as Client;
    },
    onSuccess: async (_data, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      await queryClient.invalidateQueries({ queryKey: ["clients", vars.id] });
      await queryClient.invalidateQueries({ queryKey: ["client_ar_movements"] });
      toast.success("Client updated");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to update client"),
  });
}

/** Keeps the opening AR movement + outstanding in sync with the client master fields. */
async function syncOpeningBalanceMovement(
  clientId: string,
  openingBalance: number,
  asOf: string | null,
) {
  await syncClientOpeningBalance(clientId, openingBalance, asOf);
}

export function useToggleClientActive() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from("clients")
        .update({ is_active, updated_by: user?.id ?? null })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Status updated");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to update status"),
  });
}

export function useBulkSetClientActive() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ ids, is_active }: { ids: string[]; is_active: boolean }) => {
      const { error } = await supabase
        .from("clients")
        .update({ is_active, updated_by: user?.id ?? null })
        .in("id", ids);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Clients updated");
    },
    onError: (error: Error) => toast.error(error.message || "Bulk update failed"),
  });
}

export function useDeleteClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("clients").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Client deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete client"),
  });
}

// --- Contacts ---

export function useClientContacts(clientId: string | undefined) {
  return useQuery({
    queryKey: ["clients", clientId, "contacts"],
    enabled: isSupabaseConfigured && Boolean(clientId),
    queryFn: async (): Promise<ClientContact[]> => {
      const { data, error } = await supabase
        .from("client_contacts")
        .select("*")
        .eq("client_id", clientId!)
        .order("is_primary", { ascending: false })
        .order("name");
      if (error) throw error;
      return (data ?? []).map((c) => ({ ...(c as ClientContact), tags: (c as ClientContact).tags ?? [] }));
    },
  });
}

async function clearOtherPrimaries(clientId: string, exceptId?: string) {
  let q = supabase.from("client_contacts").update({ is_primary: false }).eq("client_id", clientId).eq("is_primary", true);
  if (exceptId) q = q.neq("id", exceptId);
  const { error } = await q;
  if (error) throw error;
}

export function useCreateContact(clientId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (values: ContactFormValues) => {
      if (values.is_primary) await clearOtherPrimaries(clientId);
      const { data, error } = await supabase
        .from("client_contacts")
        .insert({
          client_id: clientId,
          name: values.name.trim(),
          designation: values.designation?.trim() || null,
          department: values.department?.trim() || null,
          mobile: values.mobile?.trim() || null,
          alt_mobile: values.alt_mobile?.trim() || null,
          email: values.email?.trim() || null,
          tags: values.tags,
          is_primary: values.is_primary,
          is_active: values.is_active,
          created_by: user?.id ?? null,
          updated_by: user?.id ?? null,
        })
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "contacts"] });
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "counts"] });
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Contact added");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to add contact"),
  });
}

export function useUpdateContact(clientId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, values }: { id: string; values: ContactFormValues }) => {
      if (values.is_primary) await clearOtherPrimaries(clientId, id);
      const { data, error } = await supabase
        .from("client_contacts")
        .update({
          name: values.name.trim(),
          designation: values.designation?.trim() || null,
          department: values.department?.trim() || null,
          mobile: values.mobile?.trim() || null,
          alt_mobile: values.alt_mobile?.trim() || null,
          email: values.email?.trim() || null,
          tags: values.tags,
          is_primary: values.is_primary,
          is_active: values.is_active,
          updated_by: user?.id ?? null,
        })
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "contacts"] });
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Contact updated");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to update contact"),
  });
}

export function useDeleteContact(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("client_contacts").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "contacts"] });
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "counts"] });
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Contact deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete contact"),
  });
}

// --- Addresses ---

export function useClientAddresses(clientId: string | undefined) {
  return useQuery({
    queryKey: ["clients", clientId, "addresses"],
    enabled: isSupabaseConfigured && Boolean(clientId),
    queryFn: async (): Promise<ClientAddress[]> => {
      const { data, error } = await supabase
        .from("client_addresses")
        .select("*")
        .eq("client_id", clientId!)
        .order("is_default", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ClientAddress[];
    },
  });
}

export function useCreateAddress(clientId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (values: AddressFormValues) => {
      const { data, error } = await supabase
        .from("client_addresses")
        .insert({
          client_id: clientId,
          title: values.title.trim(),
          addr1: values.addr1.trim(),
          addr2: values.addr2?.trim() || null,
          city: values.city.trim(),
          state: values.state.trim(),
          pincode: values.pincode.trim(),
          address_type: values.address_type,
          is_default: values.is_default,
          notes: values.notes?.trim() || null,
          created_by: user?.id ?? null,
          updated_by: user?.id ?? null,
        })
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "addresses"] });
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "counts"] });
      toast.success("Address added");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to add address"),
  });
}

export function useUpdateAddress(clientId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, values }: { id: string; values: AddressFormValues }) => {
      const { data, error } = await supabase
        .from("client_addresses")
        .update({
          title: values.title.trim(),
          addr1: values.addr1.trim(),
          addr2: values.addr2?.trim() || null,
          city: values.city.trim(),
          state: values.state.trim(),
          pincode: values.pincode.trim(),
          address_type: values.address_type,
          is_default: values.is_default,
          notes: values.notes?.trim() || null,
          updated_by: user?.id ?? null,
        })
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "addresses"] });
      toast.success("Address updated");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to update address"),
  });
}

export function useDeleteAddress(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("client_addresses").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "addresses"] });
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "counts"] });
      toast.success("Address deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete address"),
  });
}

// --- Notes ---

export function useClientNotes(clientId: string | undefined) {
  return useQuery({
    queryKey: ["clients", clientId, "notes"],
    enabled: isSupabaseConfigured && Boolean(clientId),
    queryFn: async (): Promise<ClientNote[]> => {
      const { data, error } = await supabase
        .from("client_notes")
        .select("*")
        .eq("client_id", clientId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ClientNote[];
    },
  });
}

export function useCreateNote(clientId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (values: NoteFormValues) => {
      const { data, error } = await supabase
        .from("client_notes")
        .insert({ client_id: clientId, body: values.body.trim(), created_by: user?.id ?? null })
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "notes"] });
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "counts"] });
      toast.success("Note added");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to add note"),
  });
}

export function useDeleteNote(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("client_notes").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "notes"] });
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "counts"] });
      toast.success("Note deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete note"),
  });
}

// --- Reminders ---

export function useClientReminders(clientId: string | undefined) {
  return useQuery({
    queryKey: ["clients", clientId, "reminders"],
    enabled: isSupabaseConfigured && Boolean(clientId),
    queryFn: async (): Promise<ClientReminder[]> => {
      const { data, error } = await supabase
        .from("client_reminders")
        .select("*")
        .eq("client_id", clientId!)
        .order("is_done")
        .order("due_at", { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as ClientReminder[];
    },
  });
}

export function useCreateReminder(clientId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (values: ReminderFormValues) => {
      const { data, error } = await supabase
        .from("client_reminders")
        .insert({
          client_id: clientId,
          title: values.title.trim(),
          due_at: values.due_at?.trim() ? new Date(values.due_at).toISOString() : null,
          is_done: values.is_done,
          created_by: user?.id ?? null,
          assigned_to: user?.id ?? null,
        })
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "reminders"] });
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "counts"] });
      toast.success("Reminder added");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to add reminder"),
  });
}

export function useToggleReminder(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, is_done }: { id: string; is_done: boolean }) => {
      const { error } = await supabase.from("client_reminders").update({ is_done }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "reminders"] });
    },
    onError: (error: Error) => toast.error(error.message || "Failed to update reminder"),
  });
}

export function useDeleteReminder(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("client_reminders").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "reminders"] });
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "counts"] });
      toast.success("Reminder deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete reminder"),
  });
}

// --- Files ---

export function useClientFiles(clientId: string | undefined) {
  return useQuery({
    queryKey: ["clients", clientId, "files"],
    enabled: isSupabaseConfigured && Boolean(clientId),
    queryFn: async (): Promise<ClientFile[]> => {
      const { data, error } = await supabase
        .from("client_files")
        .select("*")
        .eq("client_id", clientId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ClientFile[];
    },
  });
}

export function useUploadClientFile(clientId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (file: File) => {
      const path = `${clientId}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
      const { error: upErr } = await supabase.storage.from("client-files").upload(path, file, {
        upsert: false,
        contentType: file.type || undefined,
      });
      if (upErr) throw upErr;

      const { data, error } = await supabase
        .from("client_files")
        .insert({
          client_id: clientId,
          name: file.name,
          storage_path: path,
          mime: file.type || null,
          size_bytes: file.size,
          uploaded_by: user?.id ?? null,
        })
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "files"] });
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "counts"] });
      toast.success("File uploaded");
    },
    onError: (error: Error) => toast.error(error.message || "Upload failed"),
  });
}

export function useDeleteClientFile(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: ClientFile) => {
      await supabase.storage.from("client-files").remove([file.storage_path]);
      const { error } = await supabase.from("client_files").delete().eq("id", file.id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "files"] });
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "counts"] });
      toast.success("File deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete file"),
  });
}

export async function getClientFileUrl(path: string) {
  const { data, error } = await supabase.storage.from("client-files").createSignedUrl(path, 3600);
  if (error) throw error;
  return data.signedUrl;
}

/** Next free sequential client code, e.g. CL-0007 (honors company.client_prefix). */
export function useNextClientCode(enabled: boolean) {
  return useQuery({
    queryKey: ["clients", "next-code"],
    enabled: isSupabaseConfigured && enabled,
    staleTime: 0,
    queryFn: async () => {
      const { data: company } = await supabase
        .from("company")
        .select("client_prefix")
        .limit(1)
        .maybeSingle();
      const prefix = (company?.client_prefix as string | undefined)?.trim().toUpperCase() || "CL";
      const { data, error } = await supabase
        .from("clients")
        .select("code")
        .ilike("code", `${prefix}-%`)
        .order("code", { ascending: false })
        .limit(1);
      if (error) throw error;
      const last = data?.[0]?.code as string | undefined;
      const n = last ? Number.parseInt(last.replace(`${prefix}-`, ""), 10) : 0;
      return `${prefix}-${String((Number.isNaN(n) ? 0 : n) + 1).padStart(4, "0")}`;
    },
  });
}

export function useClientArMovements(range?: { from: string; to: string }) {
  return useQuery({
    queryKey: ["client_ar_movements", range?.from, range?.to],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<ClientArMovement[]> => {
      let q = supabase
        .from("client_ar_movements")
        .select("*")
        .order("movement_date", { ascending: true });
      if (range?.from) q = q.gte("movement_date", range.from);
      if (range?.to) q = q.lte("movement_date", range.to);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []).map((row) => ({
        ...(row as ClientArMovement),
        amount: money((row as ClientArMovement).amount),
        source_type: (row as ClientArMovement).source_type ?? null,
        source_id: (row as ClientArMovement).source_id ?? null,
      }));
    },
  });
}

/** Ledger rows for one client (optionally filtered by date). */
export function useClientLedger(clientId?: string, range?: { from?: string; to?: string }) {
  return useQuery({
    queryKey: ["client_ar_movements", clientId, range?.from, range?.to],
    enabled: isSupabaseConfigured && Boolean(clientId),
    queryFn: async (): Promise<ClientArMovement[]> => {
      let q = supabase
        .from("client_ar_movements")
        .select("*")
        .eq("client_id", clientId!)
        .order("movement_date", { ascending: true })
        .order("created_at", { ascending: true });
      if (range?.from) q = q.gte("movement_date", range.from);
      if (range?.to) q = q.lte("movement_date", range.to);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []).map((row) => ({
        ...(row as ClientArMovement),
        amount: money((row as ClientArMovement).amount),
        source_type: (row as ClientArMovement).source_type ?? null,
        source_id: (row as ClientArMovement).source_id ?? null,
      }));
    },
  });
}

export function usePostClientAdjustment(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: {
      amount: number;
      movementDate: string;
      reference?: string;
      notes?: string;
    }) => {
      return postPartyMovement({
        party: "client",
        partyId: clientId,
        kind: "adjustment",
        amount: values.amount,
        movementDate: values.movementDate,
        reference: values.reference ?? "Adjustment",
        notes: values.notes,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["client_ar_movements"] });
      await queryClient.invalidateQueries({ queryKey: ["clients", clientId] });
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Adjustment posted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to post adjustment"),
  });
}
